import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { MessageSquarePlus, Send, X } from "lucide-react";
import { z } from "zod";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { FunctionsHttpError } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { safeLocalStorage } from "@/lib/safeStorage";
import { isFocusedView } from "@/lib/focusedViewRoutes";

const feedbackSchema = z.object({
  message: z.string().trim().min(3, "Please share a little more").max(2000, "Please keep it under 2000 characters"),
  email: z.string().trim().email("Enter a valid email").max(255).optional().or(z.literal("")),
});

const DISMISS_KEY = "bb:feedback-dismissed";
const LAST_SEEN_KEY = "bb:feedback-last-prompt";
const FIRST_VISIT_KEY = "bb:feedback-first-visit";
const AUTO_PROMPT_DELAY_MS = 60_000; // 1 minute of use on next session

/**
 * Persistent floating feedback button (bottom-right, all pages).
 * Auto-opens after 1 minute of use on the user's *next* session, once.
 * User can dismiss permanently ("Don't show again") or defer ("Later").
 */
export const FeedbackWidget = () => {
  const { user } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [cookieBannerVisible, setCookieBannerVisible] = useState(false);

  // Hidden on activity details (it collides with the discussion Post button) and
  // on other people's profiles — both are focused views reached from a list.
  const hiddenRoute =
    location.pathname === "/admin" ||
    location.pathname.startsWith("/messages/") ||
    isFocusedView(location.pathname, user?.id);

  // Move above the cookie banner while it's visible.
  useEffect(() => {
    const onBanner = (e: Event) => setCookieBannerVisible(Boolean((e as CustomEvent).detail));
    window.addEventListener("bb:cookie-banner", onBanner);
    return () => window.removeEventListener("bb:cookie-banner", onBanner);
  }, []);

  // Auto-prompt logic: only on the *next* session after the first visit.
  useEffect(() => {
    if (hiddenRoute) return;
    if (safeLocalStorage.getItem(DISMISS_KEY) === "1") return;

    const firstVisit = safeLocalStorage.getItem(FIRST_VISIT_KEY);
    const now = Date.now();

    if (!firstVisit) {
      // Mark first visit; don't auto-open this session.
      safeLocalStorage.setItem(FIRST_VISIT_KEY, String(now));
      return;
    }

    // Already prompted before? Don't nag again automatically.
    if (safeLocalStorage.getItem(LAST_SEEN_KEY)) return;

    // Second session: schedule auto-open after 1 minute of use.
    const timer = window.setTimeout(() => {
      if (safeLocalStorage.getItem(DISMISS_KEY) === "1") return;
      safeLocalStorage.setItem(LAST_SEEN_KEY, String(Date.now()));
      setOpen(true);
    }, AUTO_PROMPT_DELAY_MS);

    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (hiddenRoute) return null;

  const handleLater = () => {
    setOpen(false);
  };

  const handleDismissForever = () => {
    safeLocalStorage.setItem(DISMISS_KEY, "1");
    setOpen(false);
    toast.success("Got it — we won't ask again.");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const parsed = feedbackSchema.safeParse({ message, email });
    if (!parsed.success) {
      const firstError = parsed.error.issues[0]?.message ?? "Please check your input";
      toast.error(firstError);
      return;
    }
    setSubmitting(true);
    try {
      const { error } = await supabase.functions.invoke("notify-admins-feedback", {
        body: {
          message: parsed.data.message,
          email: parsed.data.email || undefined,
          pagePath: location.pathname + location.search,
          userAgent: typeof navigator !== "undefined" ? navigator.userAgent.slice(0, 500) : undefined,
        },
      });

      if (error) {
        const details = error instanceof FunctionsHttpError ? await error.context.text() : error.message;
        throw new Error(details);
      }

      toast.success("Thanks — your feedback helps shape Balham Babes 💜");
      safeLocalStorage.setItem(DISMISS_KEY, "1"); // don't re-prompt after submission
      setMessage("");
      setEmail("");
      setOpen(false);
    } catch (err) {
      console.error("feedback submit failed", err);
      toast.error("Couldn't send feedback. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <button
        type="button"
        aria-label="Share feedback"
        title="Share feedback"
        onClick={() => setOpen(true)}
        className={`fixed z-40 right-4 ${cookieBannerVisible ? "bottom-[18.5rem] md:bottom-32" : "md:bottom-6 bottom-[calc(5.75rem+env(safe-area-inset-bottom,0px))]"} flex items-center justify-center gap-2 rounded-full bg-primary text-primary-foreground h-12 w-12 md:h-auto md:w-auto md:px-4 md:py-2.5 text-sm font-medium shadow-lg hover:shadow-xl hover:scale-105 active:scale-95 transition-all`}
      >
        <MessageSquarePlus className="h-5 w-5 md:h-4 md:w-4" />
        <span className="hidden md:inline">Feedback</span>
      </button>




      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader className="pr-10 text-left">
            <DialogTitle>Share your feedback</DialogTitle>
            <DialogDescription>
              Your input shapes what we build next.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="feedback-message">What could be improved or is missing?</Label>
              <Textarea
                id="feedback-message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Tap here to tell us what would make Balham Babes better…"
                rows={5}
                maxLength={2000}
                required
              />
              <p className="text-xs text-muted-foreground text-right">{message.length}/2000</p>
            </div>

            {!user && (
              <div className="space-y-2">
                <Label htmlFor="feedback-email">Email (optional)</Label>
                <Input
                  id="feedback-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  maxLength={255}
                />
                <p className="text-xs text-muted-foreground">Only if you'd like us to reply.</p>
              </div>
            )}

            <div className="flex flex-col gap-3 pt-3 border-t">
              <Button type="submit" disabled={submitting || message.trim().length < 3} className="w-full">
                <Send className="h-4 w-4 mr-2" />
                {submitting ? "Sending…" : "Send feedback"}
              </Button>
              <div className="flex items-center justify-between gap-2">
                <Button type="button" variant="ghost" size="sm" onClick={handleLater} disabled={submitting} className="text-muted-foreground">
                  Later
                </Button>
                <Button type="button" variant="ghost" size="sm" onClick={handleDismissForever} disabled={submitting} className="text-muted-foreground">
                  <X className="h-4 w-4 mr-1" /> Don't show again
                </Button>
              </div>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default FeedbackWidget;
